import { useEffect, useState } from 'react';
import {
  DTOgetOrderDetailedData,
  ORDER_STATUS,
  PAYMENT_TYPES,
  PAYMENTS_PROVIDER,
  TYPES_OF_BILLING
} from '../../../../../types/orders';
import { NudosButton, NudosFileInput, NudosTextInput } from '../../../../../components/NudosComponents';
import { GrayArrow, InformationIcon } from '../../../../../assets/images/shopping';
import { ShipmentsCostsSection, AdditionalTaxComponent } from './Components';
import { updateNewCreditDate, updateVoucher } from '../../../../../services/orders';
import { formatCost } from '../../../../../utils/formatNumbers';
import NudosDatePicker from '../../../../../components/NudosComponents/NudosDatePicker';
import {
  formatErrorTextForImageDocuments,
  isFileValid,
  validFormatsForDocumentsIncludingImages
} from '../../../../../utils/validateFiles';
import OrganizationInfoSection from './Components/OrganizationInfoSection/OrganizationInfoSection';
import { SPECIAL_BUSINESS_NAME_TYPES_ID } from '../../../../../utils/productDefinitions';
import { getPaymentMethodName } from '../../../../LogisticServices/utils/general.utils';
import PaymentDetailsModal from '../../../../../components/PaymentDetailsModal/PaymentDetailsModal';
import { submitComment } from '../../../../LogisticServices/utils/general.utils';
import { IconArrowDown } from '../../../../../assets/DesignSystem/SVGComponents';
import CommentsBox from '../../../Components/CommentsBox/CommentsBox';
import { ICommentsAndObservations } from '../../../../../types/global';
import useCommentsState from '../../../../../state/useCommentsState';
import { IResponsibleUserData } from '../../../../../types/responsibleUser.types';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../utils/displayNudosStandardNotifications';
import {
  assignOrderResponsibleUser,
  deleteOrderResponsibleUser,
  getOrderResponsibleUsersList
} from '../../../../../services/responsibleUsers.service';
import { IbackofficeUserData } from '../../../../../types/configuration.types';
import CreditDaysCounter from '../../../../../components/CreditDaysCounter/CreditDaysCounter';
import { IorganizationBusinessData } from 'types/bussines';
import { getOrganizationData } from 'services/bussines';

import './Tab1General.scss';

const Tab1General = ({
  changeStatusModalLink,
  updateWithoutLoaders,
  updateData,
  updateWithLoaders,
  orderDetailedData
}: {
  changeStatusModalLink?: () => void;
  updateWithoutLoaders: () => Promise<void>;
  updateData: () => void;
  updateWithLoaders?: () => Promise<void>;
  orderDetailedData?: DTOgetOrderDetailedData;
}) => {
  const [voucher, setVoucher] = useState<string>();
  const [loadFile, setLoadFile] = useState<boolean>(false);
  const [creditDate, setCreditDate] = useState<string>();
  const [isEditCreditDate, setIsEditCreditDate] = useState<boolean>(false);
  const [loadingCredit, setLoadingCredit] = useState<boolean>(false);
  const [showBankAccountInfoModal, setShowBankAccountInfoModal] = useState<boolean>(false);
  const [showComments, setShowComments] = useState<boolean>(true);
  const { comments, loadingComments, setLoadingComments } = useCommentsState();
  const [responsibleUsersList, setResponsibleUsersList] = useState<IResponsibleUserData[]>([]);
  const [loadingResponsibleUsersList, setLoadingResponsibleUsersList] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const [organizationInfo, setOrganizationInfo] = useState<IorganizationBusinessData | undefined>(undefined);
  const {
    generalData,
    orderId,
    billingData,
    billingMethod,
    hasActiveCredit = false,
    statusName,
    orderComments,
    couponDiscountAmount,
    orderDates
  } = orderDetailedData || {};

  const { countryBilling, organizationId } = billingData || {};
  const { billingProvider } = billingMethod || {};
  const { currency, exchangeRate, wasPaymentAutomationAttempted, paymentMethod, paymentAgreementDays } =
    generalData?.paymentData || {};
  const { bankAccountInfo, paymentData } = generalData || {};
  const specialBusinessType = billingData?.specialBusinessNameTypeId === SPECIAL_BUSINESS_NAME_TYPES_ID?.TRADING;

  const billingCountryIsArgentina = countryBilling === 'Argentina';
  const billingCountryIsBrasil = countryBilling === 'Brasil';

  const shipmentsTotalCostText = `$${paymentData?.shipmentTotalUsd || 0} USD`;
  const isProofOfPaymentAValidLink = !!voucher?.includes('http');
  const paymentThroughPaymentIntegration = voucher?.includes(PAYMENTS_PROVIDER);

  const displayViewPaymentMethodsModal =
    paymentMethod?.includes('Transf') && bankAccountInfo && !!Object.keys(bankAccountInfo).length;
  const paymentDetailsModalData = { bankAccountInfo, ...paymentData };

  const thePaymentWasMadeByCard = paymentMethod?.toLowerCase()?.includes('tarjeta');
  const thePaymentWasMadeBordCredit = !!paymentMethod?.toLowerCase()?.includes('crédito bord');
  const daysOfThePaymentAgreement = Number(paymentAgreementDays || 0);

  const isStripePayment =
    billingProvider &&
    !hasActiveCredit &&
    [
      TYPES_OF_BILLING?.STRIPE_USA_NUDOS_CORP,
      TYPES_OF_BILLING?.STRIPE_USA_NUDOS_LLC,
      TYPES_OF_BILLING.GIGSTACK
    ].includes(billingProvider as TYPES_OF_BILLING);

  const proofOfPaymentLinkText = paymentThroughPaymentIntegration
    ? `Pago realizado por ${PAYMENTS_PROVIDER}`
    : 'Ver comprobante';

  const getProofOfPaymentLinkStyles = () => {
    if (isProofOfPaymentAValidLink) return 'viewFileBlue';
    if (paymentThroughPaymentIntegration) return 'paymentsProviderText';
    return 'viewFile';
  };

  const localPaymentMethodsWithExceptions = ['USD', 'ARS', 'BRL'].includes(currency || '');

  const isAlreadyPaid = paymentThroughPaymentIntegration || voucher;

  const hasPaymentAgreement =
    ((orderComments && !isAlreadyPaid) || paymentData?.committedPaymentDate) && !thePaymentWasMadeByCard;
  const showExchangeRate = !specialBusinessType && currency && !localPaymentMethodsWithExceptions && exchangeRate;
  const paymentAutomationText = wasPaymentAutomationAttempted ? ' - Automatizado ' : ' - Manual ';

  const internationalCreditCards = [PAYMENT_TYPES.INTERNATIONAL_CARD].includes(
    paymentData?.paymentMethod as PAYMENT_TYPES
  );

  const paymentDate = orderDates?.paymentDate;

  const automatedPaymentDoesNotShowLink = internationalCreditCards && !!paymentDate;
  const validationsToDisplayPaymentLink =
    (paymentData?.paymentMethod === PAYMENT_TYPES.INTERNATIONAL_CARD ||
      generalData?.paymentData.paymentMethod === PAYMENT_TYPES.CARD_COP) &&
    !automatedPaymentDoesNotShowLink;

  const openUrl = () => {
    if (!voucher || !isProofOfPaymentAValidLink) return;
    window.open(voucher, '_blank');
  };

  const generateTemporalUrl = (file: File) => URL.createObjectURL(file);

  const getDetails = (typeOfBilling?: TYPES_OF_BILLING) => {
    if (typeOfBilling === TYPES_OF_BILLING.STRIPE_USA_NUDOS_LLC) return ' (LLC)';
    if (typeOfBilling === TYPES_OF_BILLING.STRIPE_USA_NUDOS_CORP) return ' (C-Corp)';
    if (!specialBusinessType && countryBilling && paymentData?.currency === 'USD') return <>&nbsp;({countryBilling})</>;
  };

  const uploadFile = async (file?: File) => {
    if (!file) return;
    const validFile = isFileValid(
      file,
      validFormatsForDocumentsIncludingImages,
      10,
      formatErrorTextForImageDocuments,
      'El documento no debe superar los 10MB'
    );
    if (!validFile) return;
    setLoadFile(true);
    const temporalUrl = generateTemporalUrl(file);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const dataFile = await updateVoucher(String(orderId), formData);
      if (dataFile) {
        setVoucher(temporalUrl);
        await updateWithoutLoaders();
      }
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadFile(false);
    }
  };

  const updateCreditDate = async () => {
    setLoadingCredit(true);
    try {
      await updateNewCreditDate(Number(orderId), creditDate || '');
      await updateWithoutLoaders();
      setIsEditCreditDate(false);
      displaySuccessNotification({ customJSXMessage: <>La fecha de compromiso se guardo correctamente</>, });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingCredit(false);
    }
  };

  const handleShowComment = () => setShowComments(true);
  const handleHideComment = () => setShowComments(false);

  const getOrganizationInfo = async (): Promise<void> => {
    if (!organizationId) return;
    try {
      const response = await getOrganizationData(Number(organizationId));
      if (response) setOrganizationInfo(response);
    } catch (error) {
      displayErrorNotification();
    }
  };

  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    setLoadingComments(true);
    try {
      if (!orderId) return;
      const kamUserId = Number(organizationInfo?.assignedKam?.id);
      const commentToSend = {
        orderId: orderId as string | number,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comment.comments,
        mentions: comment.mentions || [],
        commentImage: comment?.commentImage,
        ...(kamUserId && { kamUserId: kamUserId })
      };

      await submitComment(commentToSend);

      await updateWithoutLoaders();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingComments(false);
    }
  };

  const handleGetOrderResponsibleUsersList = async () => {
    if (!orderId) return;
    setLoadingResponsibleUsersList(true);
    try {
      const response = await getOrderResponsibleUsersList(Number(orderId));
      if (response) setResponsibleUsersList(response);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingResponsibleUsersList(false);
    }
  };

  const handleAssignOrderResponsible = async (user: IbackofficeUserData) => {
    if (!orderId || !user) return;
    setLoadingStates(true);
    try {
      const orderBody = {
        orderId: Number(orderId),
        userId: user.id
      };
      const response = await assignOrderResponsibleUser(orderBody);
      if (response) handleGetOrderResponsibleUsersList();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingStates(false);
    }
  };

  const handleDeleteOrderResponsible = async (responsibleId?: number, userName?: string) => {
    if (!orderId || !responsibleId) return;
    setLoadingStates(true);
    try {
      await deleteOrderResponsibleUser(responsibleId);
      setResponsibleUsersList(responsibleUsersList.filter(user => user.id !== responsibleId));
      handleGetOrderResponsibleUsersList();
      displaySuccessNotification({
        customJSXMessage: <div>Se eliminó al responsable {userName} de esta orden exitosamente</div>
      });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    handleGetOrderResponsibleUsersList();
    getOrganizationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (paymentData?.proofOfPayment) setVoucher(paymentData?.proofOfPayment || '');
    if (paymentData?.committedPaymentDate) {
      setCreditDate(paymentData?.committedPaymentDate || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalData]);

  return (
    <div className="tab1General">
      {showBankAccountInfoModal && (
        <PaymentDetailsModal
          paymentDetailsModalData={paymentDetailsModalData}
          closeModalCallback={() => setShowBankAccountInfoModal(false)}
        />
      )}
      <OrganizationInfoSection
        orderGeneralData={generalData}
        orderId={Number(orderId)}
        responsibleUsersList={responsibleUsersList}
        handleAssignOrderResponsible={handleAssignOrderResponsible}
        handleDeleteOrderResponsible={handleDeleteOrderResponsible}
        loadingResponsibleUsersList={loadingResponsibleUsersList}
        loadingStates={loadingStates}
      />
      <div className="clientInfo">
        <div className="generalTitle mb-8">Información del cliente</div>
        <div className="clientInfoDetail">
          <div className="mediumInput">
            <NudosTextInput
              label="Nombre"
              isFilled
              isDeactivated
              placeholder=""
              defaultValue={generalData?.userData?.firstName}
              styleLabel="customInputLabel"
            />
          </div>
          <div className="mediumInput">
            <NudosTextInput
              label="Apellido"
              isFilled
              isDeactivated
              placeholder=""
              defaultValue={generalData?.userData?.lastName}
              styleLabel="customInputLabel"
            />
          </div>
          <div className="smallInput">
            <div className="containerFlag">
              <div className="countryTitle">Número de contacto</div>
              <div className="containerCountry">
                <div className="myFlag" style={{ backgroundImage: `url(${generalData?.userData?.phoneData?.flag})` }} />
                <div className="floatArrow">
                  <GrayArrow />
                </div>
              </div>
            </div>
          </div>
          <div className="bigInput">
            <NudosTextInput
              isFilled
              isDeactivated
              placeholder={''}
              falseLabel
              defaultValue={generalData?.userData?.phoneData?.phone}
              styleLabel="customInputLabel"
            />
          </div>
          <div className="extraBigInput">
            <NudosTextInput
              label="Correo"
              isFilled
              isDeactivated
              placeholder=""
              defaultValue={generalData?.userData?.email}
              styleLabel="customInputLabel"
            />
          </div>
        </div>
        <div className="bigGrayLine" />
      </div>
      <div className="paymentInfo">
        <div className="generalTitle mb-8">Información de pago</div>
        <div className="labelPaymentMethod">
          {getPaymentMethodName(paymentData?.paymentMethod || '')}
          {getDetails(billingProvider as TYPES_OF_BILLING)}
          <div className="automationText">{paymentAutomationText}</div>
          {showExchangeRate && (
            <div className="font-normal">{` (tasa de cambio: ${paymentData?.exchangeRate || ''}${
              paymentData?.currency
            })`}</div>
          )}
          {displayViewPaymentMethodsModal && (
            <div className="viewBankAccountInfo" onClick={() => setShowBankAccountInfoModal(true)}>
              <InformationIcon className="informationIcon" />
              Ver datos de pago
            </div>
          )}
        </div>
        <div className="paymentInfoDetail">
          {!!currency && !localPaymentMethodsWithExceptions && (
            <div className="bigInput">
              <NudosTextInput
                label={`Total ${paymentData?.currency}`}
                isFilled
                isDeactivated
                placeholder=""
                defaultValue={`$${formatCost(Number(paymentData?.total || '0'))} `}
                styleLabel="customInputLabel"
              />
            </div>
          )}
          <div className="bigInput">
            <NudosTextInput
              label="Total USD"
              isFilled
              isDeactivated
              placeholder=""
              defaultValue={`$${formatCost(Number(paymentData?.totalUsd || '0'))}`}
              styleLabel="customInputLabel"
            />
          </div>
          <div className="mediumInput">
            <NudosTextInput
              label="Subtotal USD"
              isFilled
              isDeactivated
              placeholder=""
              defaultValue={`$${formatCost(Number(paymentData?.subTotalUsd || 0))}`}
              styleLabel="customInputLabel"
            />
          </div>
          <div className="smallInput">
            <NudosTextInput
              label="Comision"
              isFilled
              isDeactivated
              placeholder=""
              defaultValue={`${formatCost(Number(paymentData?.commission || '0'))}%`}
              styleLabel="customInputLabel"
            />
          </div>
          <div className="smallInput">
            <NudosTextInput
              label="Envío total"
              isFilled
              isDeactivated
              placeholder={shipmentsTotalCostText}
              defaultValue={shipmentsTotalCostText}
              styleLabel="customInputLabel"
            />
          </div>
          {couponDiscountAmount && (
            <NudosTextInput
              label="Descuento por cupón"
              isFilled
              customClassName="couponDiscountInput"
              isDeactivated
              placeholder={`$${formatCost(Number(couponDiscountAmount || '0'))} USD`}
              styleLabel="customInputLabel"
            />
          )}
          {validationsToDisplayPaymentLink && (
            <div className="loadPaymentLink" onClick={() => changeStatusModalLink?.()}>
              {paymentData?.paymentLink ? 'Ver link de pago  👍🏻' : 'Cargar link de pago⚠️'}{' '}
            </div>
          )}
        </div>
        <AdditionalTaxComponent
          showComponent={
            (!!billingCountryIsArgentina || !!billingCountryIsBrasil) && !!orderId && !!generalData?.paymentData
          }
          currencyCode={billingCountryIsBrasil ? 'BRL' : 'ARS'}
          orderId={Number(orderId)}
          paymentData={generalData?.paymentData}
          updateWithLoaders={updateWithLoaders}
          disabledComponent={statusName === ORDER_STATUS.CANCELLED}
        />
        <div className="paymentDownDetail">
          <div className="bigUpload">
            <NudosFileInput
              fakeLabel
              uploadedFileHandler={file => uploadFile(file)}
              color="orange"
              buttonText={
                orderComments && paymentData?.committedPaymentDate && !voucher
                  ? 'Carga el comprobante para eliminar la deuda'
                  : 'Comprobante'
              }
              isFilled={isProofOfPaymentAValidLink}
              isDisabled={paymentThroughPaymentIntegration}
              isLoading={loadFile}
              errorStyles={!!orderComments && !!paymentData?.committedPaymentDate && !voucher}
            />
          </div>
          <div className={getProofOfPaymentLinkStyles()} onClick={openUrl}>
            {proofOfPaymentLinkText}
          </div>
          {hasPaymentAgreement && (
            <>
              <div className="containerDateVoucher">
                <NudosDatePicker
                  updateCurrentValueCallback={e => {
                    setCreditDate(e);
                    setIsEditCreditDate(true);
                  }}
                  defaultValue={paymentData?.committedPaymentDate}
                  label="Fecha de compromiso de pago"
                  isFilled={!!creditDate}
                  desiredTimeZone={undefined} // MX by default, since its information used by operations
                />
              </div>
              <div className="saveDateVoucher">
                <NudosButton
                  isButtonDisabled={!creditDate || !isEditCreditDate || loadingCredit}
                  customClassName="voucherButton"
                  buttonText="Guardar"
                  customHeight="18px"
                  customWidth="80px"
                  isButtonLoading={loadingCredit}
                  handleClick={() => updateCreditDate()}
                />
              </div>
            </>
          )}
        </div>
        {isStripePayment && !paymentThroughPaymentIntegration && !isProofOfPaymentAValidLink && (
          <div className="blueText">Pago pendiente de verificar por Stripe</div>
        )}
        <CreditDaysCounter
          customClassCreditDaysCounter="customClassCreditDaysCounter"
          showCreditDaysCounter={thePaymentWasMadeBordCredit}
          days={daysOfThePaymentAgreement}
        />
        <ShipmentsCostsSection shipmentGroupsData={generalData?.shipmentGroupsData || []} />
        <div className="bigGrayLine" />
      </div>

      <NudosButton
        customClassName="buttonComments"
        buttonText="Chat Interno"
        handleClick={handleShowComment}
        buttonIcon={
          <div className="contentIcon">
            <IconArrowDown />
          </div>
        }
      />
      {showComments && !!organizationId && (
        <CommentsBox
          closeComments={handleHideComment}
          comments={comments || []}
          loading={loadingComments}
          handleSubmitComments={handleCommentsSubmission}
          responsibleUsers={responsibleUsersList}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};

export default Tab1General;
